import * as graphSdk from '@/api/graphql-sdk.generated'
import * as graph from '@shared/types/graphql'
import { defineModule } from 'direct-vuex'
import api from '@/api'
import { moduleActionContext, moduleGetterContext } from '..'
import { updatedDiff } from 'deep-object-diff'
import {
  RespondentGender,
  RespondentRegion,
  RespondentStandardEducation,
  RespondentStandardEmployment,
  RespondentStandardHHIUS,
  RaceCategory,
  HispanicCategory,
  RaceAndHispanicCategory,
  EthnicitySlicingThresholdDate
} from '@alpha/shared/src/entities/RespondentSession'
import {
  addAnswerToResponse,
  generateRespondentIdToResponseMap,
  generateRespondentToggleMap,
  generateResponseToggleMap,
  generateSliceByQuestionBuckets,
  getAnswerType,
  getResponsesBySegment
} from '@/components/Survey/Utilities/SurveyUtilities'
import { DoubleNestedResponses, SliceOption } from '@/components/Survey/Utilities/ChartUtilities'
import router, { sharedViewRouteName } from '@/router'
import { QuestionType, SliceType } from '@shared/types/database'

const VARIANT_LETTER_MAP : Readonly<Record<string, string>> = { 1: 'Variant A', 2: 'Variant B', 3: 'Variant C', 4: 'Variant D' }
export interface SurveyState {
  respondents: Readonly<Respondent[]>,
  survey: Readonly<Survey | null>
  questions: Readonly<Question[]>
  respondentRemovalMap: Record<string, boolean>
  responseRemovalMap: Record<string, boolean>
}

type GroupedResponses<T> =
  T extends SliceType.DEFAULT ? Record<string, Response[]> :
    T extends SliceType ? Record<string, Record<string, Response[]>> :
      never;

export type Survey = NonNullable<graphSdk.SurveyQuery['survey']>
export type Question = NonNullable<Survey['questions']>[0] & { responses?: Response[], variantRelationId?: string, displayOrder?: number }
export type Option = NonNullable<Question['options']>[0]
export type InsightsPayload = NonNullable<Survey['insights']>
export type Insight = InsightsPayload['insights'][0]
export type Respondent = NonNullable<Survey['respondents']>[0]
export type Response = NonNullable<Respondent['responses']>[0] & { answer?: any }
export type TestConfiguration = NonNullable<graphSdk.TestConfigQuery['testConfig']>

const createSurveyState = (): SurveyState => ({
  respondents: [],
  survey: null,
  questions: [],
  responseRemovalMap: {},
  respondentRemovalMap: {}
})

const survey = defineModule({
  namespaced: true,
  state: createSurveyState,
  mutations: {
    setSurvey (state, survey: Survey) {
      state.survey = Object.freeze(survey)
    },
    setRespondents (state, respondents: Respondent[]) {
      state.respondents = Object.freeze(respondents)
    },
    setResponseRemovalMap (state, responseRemovalMap: SurveyState['responseRemovalMap']) {
      state.responseRemovalMap = Object.freeze(responseRemovalMap)
    },
    setRespondentRemovalMap (state, respondentRemovalMap: SurveyState['respondentRemovalMap']) {
      state.respondentRemovalMap = Object.freeze(respondentRemovalMap)
    },
    setQuestions (state, questions: Question[]) {
      state.questions = Object.freeze(questions)
    },
    reset (state) {
      Object.assign(state, createSurveyState())
    }
  },
  getters: {
    surveyId (state: SurveyState): string | undefined {
      return state.survey?.id
    },
    insights (state: SurveyState): Insight[] {
      return state.survey?.insights?.insights || []
    },
    // Unfiltered for respondent management, includes removed respondents for display purposes
    unfilteredResponses (...context): Response[] {
      const { state } = surveyGetterContext(context)
      return state.respondents.reduce((acc: Response[], respondent) => {
        acc = acc.concat(respondent.responses || [])
        return acc
      }, [])
    },
    unfilteredResponsesByQuestion: (...context) => (questionId: string): Response[] => {
      const { getters } = surveyGetterContext(context)
      const unfilteredResponses = getters.unfilteredResponses
      return unfilteredResponses.filter((response: Response) => {
        return response.questionId === questionId
      })
    },
    unfilteredResponsesByRespondentId: (...context) => (respondentId: string): Response[] => {
      const { getters } = surveyGetterContext(context)
      return getters.respondentById(respondentId).responses!
    },
    questionsWithUnfilteredResponses (...context): Array<Question & { responses: Response[] }> {
      const { getters } = surveyGetterContext(context)
      return getters.questionsWithConfigs.map(question => {
        return {
          ...question,
          responses: getters.unfilteredResponsesByQuestion(question.id)
        }
      })
    },
    responses (...context): Response[] {
      const { state } = surveyGetterContext(context)
      return state.respondents.reduce((acc: Response[], respondent) => {
        if (state.respondentRemovalMap[respondent.id]) {
          acc = acc.concat(respondent.responses || [])
        }
        return acc
      }, [])
    },
    responsesByQuestion: (...context) => (questionId: string): Response[] => {
      const { getters } = surveyGetterContext(context)
      return getters.responses.filter((response: Response) => {
        return response.questionId === questionId
      })
    },
    respondentById: (...context) => (respondentId: string): Respondent => {
      const { state } = surveyGetterContext(context)
      return state.respondents.find(respondent => respondent.id === respondentId)!
    },
    respondentCount: (...context): number => {
      const { getters } = surveyGetterContext(context)
      const question = getters.questionsWithResponses.filter(question => question.type !== QuestionType.Description)[0]
      if (!question?.responses) return 0
      return question.responses.length
    },
    questionById: (...context) => (questionId: string): Question | undefined => {
      const { getters } = surveyGetterContext(context)
      return getters.questionsWithConfigs.find(question => question.id === questionId)
    },
    questionsWithResponses (...context): Array<Question & { responses: Response[]}> {
      const { getters } = surveyGetterContext(context)
      return getters.questionsWithConfigs.map(question => {
        return {
          ...question,
          responses: getters.responsesByQuestion(question.id)
        }
      })
    },
    questionOptionsByQuestionId: (state: SurveyState, getters) => (questionId: string) : Option[] => {
      const question: Question = getters.questionById(questionId)
      return question.options || []
    },

    /**
     * TODO: Update this
     * This is used during slicing in order to get the responses for an answer by given respondent buckets
     * responses: The responses being separated into buckets
     * respondents: A list of the respondents separated into buckets by demographic or variant { "Male": [id1, id2, id3, etc] }
     * type: The question type, used to fetch proper answer field from the responses
     */
    getResponsesForSegment: (...context) => (responses: NonNullable<Question['responses']>, sliceType: SliceType, type: QuestionType): Response[] | Record<string, Response[]> => {
      const { getters } = surveyGetterContext(context)
      const respondents = getters.respondentsBySlice(sliceType)
      if (Array.isArray(respondents)) return responses
      return getResponsesBySegment(respondents, responses, type)
    },

    getResponsesForVariant: (...context) => (responses: NonNullable<Question['responses']>, variantQuestionId: string, type: QuestionType): Record<string, Response[]> => {
      const { getters } = surveyGetterContext(context)
      const respondents: Record<string, string[]> = getters.respondentsByVariant(variantQuestionId)
      return getResponsesBySegment(respondents, responses, type)
    },

    sliceResponses: (...context) => (responses: Response[], question: Question, selectedSliceOption: SliceOption, isSliced: boolean, isSliceableQuestion: boolean): Response[] | Record<string, Response[]> | Record<string, Record<string, Response[]>> => {
      const { getters } = surveyGetterContext(context)
      const type = question.type as QuestionType
      // If it's a variant question, CAN be sliced, and IS sliced by demographic (not question...)
      if (question.variantRelationId &&
        isSliced &&
        isSliceableQuestion
      ) {
        return getters.getSlicedResponsesForVariant(responses, question.variantRelationId!, type, selectedSliceOption.value as SliceType)
      }

      // If it's a variant question
      if (question.variantRelationId) {
        return getters.getSlicedResponsesForVariant(responses, question.variantRelationId, type, selectedSliceOption.value as SliceType.DEFAULT)
      }

      // Check if it's a slice type (not slice by question)
      // This is also the fallback case that can be "None" so that the computed properties have something to initialize with
      if (Object.values(SliceType).includes(selectedSliceOption.value as SliceType)) {
        return getters.getResponsesForSegment(responses, selectedSliceOption.value as SliceType, type)
      }

      // Sliced by question
      return getters.getResponsesForSliceByQuestion(responses, selectedSliceOption.value, type)
    },

    getSlicedResponsesForVariant: (...context) => <T extends SliceType>(
      responses: NonNullable<Question['responses']>,
      variantQuestionId: string,
      type: QuestionType,
      sliceBy: T
    ) : GroupedResponses<T> => {
      const { getters } = surveyGetterContext(context)
      if (sliceBy === SliceType.DEFAULT) return getters.getResponsesForVariant(responses, variantQuestionId, type) as GroupedResponses<T>
      const slicedVariantRespondents: DoubleNestedResponses = {}
      const letterMap = { ...VARIANT_LETTER_MAP }

      // The variant options used to initialize the records
      const questionOptions: Option[] = getters.questionOptionsByQuestionId(variantQuestionId)
      // The responses for the question being sliced
      const respondentIdToResponseMap = generateRespondentIdToResponseMap(responses)
      // The responses which tell us which variant the respondent is in
      const variantRespondentIdToResponseMap = generateRespondentIdToResponseMap(getters.responsesByQuestion(variantQuestionId))

      // Iterate over each group of respondents for one demographic at a time
      const slicedRespondents: Record<string, string[]> = Object.values(SliceType).includes(sliceBy)
        ? getters.respondentsBySlice(sliceBy) as Record<string, string[]>
        : getters.respondentsByResponsesToQuestion(sliceBy) as Record<string, string[]>

      for (const [segmentName, respondentIds] of Object.entries(slicedRespondents)) {
        slicedVariantRespondents[segmentName] = {}

        // Ensure ordering of variants
        questionOptions.sort((a, b) => (a.order > b.order) ? 1 : -1)
        questionOptions.forEach((option, index) => {
          if (option.label) {
            // index + 1 because order starts at 0 but variantIds start at 1
            letterMap[index + 1] = option.label
          }

          const variantKey = letterMap[option.order + 1]
          slicedVariantRespondents[segmentName][variantKey] = []
        })

        // Iterate over the respondents for this demographic
        respondentIds.forEach(respondentId => {
          const response = respondentIdToResponseMap[respondentId]
          if (response) {
            const responseWithAnswer = addAnswerToResponse(response, type)
            const respondentResponse = variantRespondentIdToResponseMap[respondentId]
            if (!respondentResponse) return
            const variantResponse = addAnswerToResponse(respondentResponse, QuestionType.Variant)
            const key = letterMap[variantResponse.textAnswer!]
            // TODO We shouldn't need this check anymore once we get consistent N-sizes across
            // RespondentSurveys
            // https://app.clubhouse.io/alpha-hq/story/6801/verification-in-channel-for-tide-data
            if (segmentName && key) {
              slicedVariantRespondents[segmentName][key].push(responseWithAnswer)
            }
          }
        })
      }

      return slicedVariantRespondents as GroupedResponses<T>
    },

    getResponsesForSliceByQuestion: (...context) => (responses: NonNullable<Question['responses']>, questionId: string, type: QuestionType): Record<string, Response[]> => {
      const { getters } = surveyGetterContext(context)
      const respondents: Record<string, string[]> = getters.respondentsByResponsesToQuestion(questionId)
      return getResponsesBySegment(respondents, responses, type)
    },

    getTestAudience: (...context):Pick<graphSdk.Audience, '_id' | 'payload'> | undefined => {
      const { state, rootGetters, rootState } = surveyGetterContext(context)
      let audience = rootState.surveyConfiguration.testMetaDetails?.audience
      if (!audience) {
        const mgtTest = rootGetters.workspace.getMgtTestById(state.survey!.testId)
        audience = rootGetters.workspace.getAudienceById(mgtTest.data.payload.segment.id)
      }

      return audience
    },

    // RESPONDENT METHODS FOR SLICING
    respondentsBySlice: (...context) => (sliceType: SliceType): string[] | Record<string, string[]> => {
      const { getters, state } = surveyGetterContext(context)
      switch (sliceType) {
        case SliceType.AGE:
          return getters.respondentsByAge
        case SliceType.GENDER:
          return getters.respondentsByGender
        case SliceType.EDUCATION:
          return getters.respondentsByEducation
        case SliceType.STANDARD_EMPLOYMENT:
          return getters.respondentsByEmployment
        case SliceType.STANDARD_HHI_US:
          return getters.respondentsByHHI
        case SliceType.DEVICE:
          return getters.respondentsByDeviceType
        case SliceType.REGION:
          return getters.respondentsByRegion
        case SliceType.RACE:
          return getters.respondentsByRace
        case SliceType.HISPANIC_ORIGIN:
          return getters.respondentsByHispanicOrigin
        case SliceType.RACE_AND_HISPANIC_ORGIN:
          return getters.respondentsByRaceAndHispanicOrigin
        case SliceType.DEFAULT:
        default:
          return state.respondents.map(respondent => respondent.id)
      }
    },

    respondentsByRace: (...context) => {
      const { getters, state } = surveyGetterContext(context)
      const respondentsByDemo: Record<string, string[]> = {}

      Object.values(RaceCategory).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      const audience = getters.getTestAudience

      if (audience?.payload?.segment?.international || EthnicitySlicingThresholdDate > new Date(state.survey!.createdAt)) return respondentsByDemo

      for (const respondent of state.respondents) {
        if (respondent.race) respondentsByDemo[respondent.race].push(respondent.id)
      }
      return respondentsByDemo
    },

    respondentsByHispanicOrigin: (...context) => {
      const { getters, state } = surveyGetterContext(context)
      const respondentsByDemo: Record<string, string[]> = {}

      Object.values(HispanicCategory).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      const audience = getters.getTestAudience

      if (audience?.payload?.segment?.international || EthnicitySlicingThresholdDate > new Date(state.survey!.createdAt)) return respondentsByDemo

      for (const respondent of state.respondents) {
        if (respondent.hispanicOrigin) respondentsByDemo[respondent.hispanicOrigin].push(respondent.id)
      }
      return respondentsByDemo
    },

    respondentsByRaceAndHispanicOrigin: (...context) => {
      const { getters, state } = surveyGetterContext(context)
      const respondentsByDemo: Record<string, string[]> = {}

      Object.values(RaceAndHispanicCategory).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      const audience = getters.getTestAudience

      if (audience?.payload?.segment?.international || EthnicitySlicingThresholdDate > new Date(state.survey!.createdAt)) return respondentsByDemo

      for (const respondent of state.respondents) {
        if (respondent.raceAndHispanicOrigin) respondentsByDemo[respondent.raceAndHispanicOrigin].push(respondent.id)
      }
      return respondentsByDemo
    },

    respondentsByVariant: (...context) => (variantQuestionId: string): Record<string, string[]> => {
      const { getters } = surveyGetterContext(context)
      const respondentsByQuestionResponse: Record<string, string[]> = {}
      const letterMap = { ...VARIANT_LETTER_MAP }

      // Do this to ensure the ordering of variants
      const questionOptions = getters.questionOptionsByQuestionId(variantQuestionId)
      questionOptions.sort((a, b) => (a.order > b.order) ? 1 : -1)
      questionOptions.forEach((option, index) => {
        if (option.label) {
          letterMap[index + 1] = option.configuration?.title || option.label
        }

        const variantKey = letterMap[option.order + 1]
        respondentsByQuestionResponse[variantKey] = []
      })

      const variantResponses: Response[] = getters.responsesByQuestion(variantQuestionId)
      for (const response of variantResponses) {
        // response.textAnswer is the variant the respondent saw (1 min through 4 max)
        // these values will be the bucket keys
        const key = letterMap[response.textAnswer!]
        respondentsByQuestionResponse[key].push(response.respondentSurveyId!)
      }

      return respondentsByQuestionResponse
    },

    respondentsByResponsesToQuestion: (state: SurveyState, getters) => (sliceQuestionId: string) => {
      const sliceQuestion: Question = getters.questionById(sliceQuestionId)

      const respondentsByQuestionResponse: Record<string, string[]> = generateSliceByQuestionBuckets(sliceQuestion)

      const responses: Response[] = getters.responsesByQuestion(sliceQuestionId)
      for (const response of responses) {
        const answers = response[getAnswerType(sliceQuestion.type as QuestionType)]

        // Multi-select is special case here ... since they have multiple answers, we want that respondent in multiple buckets
        if (sliceQuestion.type === QuestionType.MultiSelect) {
          answers.forEach((answer: string) => {
            if (Object.keys(respondentsByQuestionResponse).includes(answer)) {
              respondentsByQuestionResponse[answer].push(response.respondentSurveyId!)
            }
          })
        } else {
          if (Object.keys(respondentsByQuestionResponse).includes(answers)) {
            respondentsByQuestionResponse[answers].push(response.respondentSurveyId!)
          }
        }
      }

      return respondentsByQuestionResponse
    },

    respondentsByGender: (state: SurveyState) => {
      const respondentsByDemo: Record<string, string[]> = {}
      Object.values(RespondentGender).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      for (const respondent of state.respondents) {
        if (respondent.gender) {
          respondentsByDemo[respondent.gender].push(respondent.id)
        }
      }
      return respondentsByDemo
    },

    respondentsByEducation: (state: SurveyState) => {
      const respondentsByDemo: Record<string, string[]> = {}

      const educationBuckets = ['No Degree', 'High School Degree or Equivalent',
        'Associates Degree', 'Bachelors Degree', 'Graduate Degree', 'Other']
      Object.values(educationBuckets).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      for (const respondent of state.respondents) {
        if (respondent.standardEducation) {
          switch (respondent.standardEducation as RespondentStandardEducation) {
            case RespondentStandardEducation.THIRD_GRADE_OR_LESS:
            case RespondentStandardEducation.MIDDLE_SCHOOL:
            case RespondentStandardEducation.SOME_HIGH_SCHOOL:
              respondentsByDemo['No Degree'].push(respondent.id)
              break
            case RespondentStandardEducation.HIGH_SCHOOL_GRADUATE:
            case RespondentStandardEducation.SOME_COLLEGE:
              respondentsByDemo['High School Degree or Equivalent'].push(respondent.id)
              break
            case RespondentStandardEducation.ASSOCIATE_DEGREE:
              respondentsByDemo['Associates Degree'].push(respondent.id)
              break
            case RespondentStandardEducation.COLLEGE_DEGREE:
            case RespondentStandardEducation.SOME_GRADUATE:
              respondentsByDemo['Bachelors Degree'].push(respondent.id)
              break
            case RespondentStandardEducation.DOCTORATE_DEGREE:
            case RespondentStandardEducation.MASTERS_DEGREE:
              respondentsByDemo['Graduate Degree'].push(respondent.id)
              break
            case RespondentStandardEducation.OTHER:
            case RespondentStandardEducation.VOCATIONAL_TRAINING:
            default:
              respondentsByDemo.Other.push(respondent.id)
              break
          }
        }
      }
      return respondentsByDemo
    },

    respondentsByHHI: (state: SurveyState) => {
      const respondentsByDemo: Record<string, string[]> = {}

      const hhiBuckets = ['$0-$24,999', '$25,000-$49,999', '$50,000-$74,999', '$75,000-$99,999',
        '$100,000-$149,999', '$150,000+', 'Prefer not to Answer']
      Object.values(hhiBuckets).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      for (const respondent of state.respondents) {
        if (respondent.standardHHIUS) {
          switch (respondent.standardHHIUS as RespondentStandardHHIUS) {
            case RespondentStandardHHIUS.N_LESS_THAN_5000:
            case RespondentStandardHHIUS.N_5000_TO_9999:
            case RespondentStandardHHIUS.N_10000_TO_14999:
            case RespondentStandardHHIUS.N_15000_TO_19999:
            case RespondentStandardHHIUS.N_20000_TO_24999:
              respondentsByDemo['$0-$24,999'].push(respondent.id)
              break
            case RespondentStandardHHIUS.N_25000_TO_29999:
            case RespondentStandardHHIUS.N_30000_TO_34999:
            case RespondentStandardHHIUS.N_35000_TO_39999:
            case RespondentStandardHHIUS.N_40000_TO_44999:
            case RespondentStandardHHIUS.N_45000_TO_49999:
              respondentsByDemo['$25,000-$49,999'].push(respondent.id)
              break
            case RespondentStandardHHIUS.N_50000_TO_54999:
            case RespondentStandardHHIUS.N_55000_TO_59999:
            case RespondentStandardHHIUS.N_60000_TO_64999:
            case RespondentStandardHHIUS.N_65000_TO_69999:
            case RespondentStandardHHIUS.N_70000_TO_74999:
              respondentsByDemo['$50,000-$74,999'].push(respondent.id)
              break
            case RespondentStandardHHIUS.N_75000_TO_79999:
            case RespondentStandardHHIUS.N_80000_TO_84999:
            case RespondentStandardHHIUS.N_85000_TO_89999:
            case RespondentStandardHHIUS.N_90000_TO_94999:
            case RespondentStandardHHIUS.N_95000_TO_99999:
              respondentsByDemo['$75,000-$99,999'].push(respondent.id)
              break
            case RespondentStandardHHIUS.N_100000_TO_124999:
            case RespondentStandardHHIUS.N_125000_TO_149999:
              respondentsByDemo['$100,000-$149,999'].push(respondent.id)
              break
            case RespondentStandardHHIUS.N_150000_TO_174999:
            case RespondentStandardHHIUS.N_175000_TO_199999:
            case RespondentStandardHHIUS.N_200000_TO_249999:
            case RespondentStandardHHIUS.N_250000_OR_MORE:
              respondentsByDemo['$150,000+'].push(respondent.id)
              break
            case RespondentStandardHHIUS.PREFER_NOT_TO_ANSWER:
            default:
              respondentsByDemo['Prefer not to Answer'].push(respondent.id)
              break
          }
        }
      }
      return respondentsByDemo
    },

    respondentsByRegion: (state: SurveyState) => {
      const respondentsByDemo: Record<string, string[]> = {}
      Object.values(RespondentRegion).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      for (const respondent of state.respondents) {
        if (respondent.region) {
          respondentsByDemo[respondent.region].push(respondent.id)
        }
      }
      return respondentsByDemo
    },

    respondentsByDeviceType: (state: SurveyState) => {
      const respondentsByDemo: Record<string, string[]> = {}

      const deviceBuckets = ['Laptop or Desktop', 'Mobile or Tablet']
      Object.values(deviceBuckets).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      for (const respondent of state.respondents) {
        if (respondent.msIsMobile) {
          respondentsByDemo['Mobile or Tablet'].push(respondent.id)
        } else {
          respondentsByDemo['Laptop or Desktop'].push(respondent.id)
        }
      }
      return respondentsByDemo
    },

    respondentsByAge: (state: SurveyState) => {
      const respondentsByDemo: Record<string, string[]> = {}

      const ageBuckets = ['18-24', '25-34', '35-44', '45-54', '55-64', '65+']
      Object.values(ageBuckets).forEach((key: string) => {
        respondentsByDemo[key] = []
      })

      for (const respondent of state.respondents) {
        if (respondent.age >= 18 && respondent.age <= 24) {
          respondentsByDemo['18-24'].push(respondent.id)
        } else if (respondent.age >= 25 && respondent.age <= 34) {
          respondentsByDemo['25-34'].push(respondent.id)
        } else if (respondent.age >= 35 && respondent.age <= 44) {
          respondentsByDemo['35-44'].push(respondent.id)
        } else if (respondent.age >= 45 && respondent.age <= 54) {
          respondentsByDemo['45-54'].push(respondent.id)
        } else if (respondent.age >= 55 && respondent.age <= 64) {
          respondentsByDemo['55-64'].push(respondent.id)
        } else {
          respondentsByDemo['65+'].push(respondent.id)
        }
      }
      return respondentsByDemo
    },

    respondentsByEmployment: (state: SurveyState) => {
      const respondentsByDemo: Record<string, string[]> = {}

      const employmentBuckets = ['Full Time', 'Part Time', 'Unemployed', 'Retired', 'Other']
      employmentBuckets.map((bucketKey: string) => {
        respondentsByDemo[bucketKey] = []
      })

      for (const respondent of state.respondents) {
        if (respondent.standardEmployment) {
          switch (respondent.standardEmployment) {
            case RespondentStandardEmployment.EMPLOYED_FULL_TIME:
            case RespondentStandardEmployment.SELF_EMPLOYED_FULL_TIME:
              respondentsByDemo['Full Time'].push(respondent.id)
              break
            case RespondentStandardEmployment.EMPLOYED_PART_TIME:
            case RespondentStandardEmployment.SELF_EMPLOYED_PART_TIME:
              respondentsByDemo['Part Time'].push(respondent.id)
              break
            case RespondentStandardEmployment.TEMPORARILY_UNEMPLOYED:
              respondentsByDemo.Unemployed.push(respondent.id)
              break
            case RespondentStandardEmployment.RETIRED:
              respondentsByDemo.Retired.push(respondent.id)
              break
            default:
              respondentsByDemo.Other.push(respondent.id)
              break
          }
        }
      }
      return respondentsByDemo
    },
    questionsWithConfigs: (...context): Question[] => {
      const { state, rootGetters } = surveyGetterContext(context)

      const questionsWithConfigs = state.questions.map((q) => {
        q.configuration = rootGetters.surveyConfiguration.questionConfigById(q.id)
        if (q.options) {
          q.options = q.options.map(o => {
            o.configuration = rootGetters.surveyConfiguration.optionConfigById(o.id)
            return o
          })
        }
        return q
      })

      return questionsWithConfigs
    }
  },
  actions: {
    reset (context) {
      const { commit } = surveyActionContext(context)
      commit.reset()
    },
    async fetchSurvey (context, payload: { workspaceId: string, testId: string, sharedLinkHash?: string }): Promise<boolean> {
      const { commit, dispatch, rootDispatch, rootCommit, getters } = surveyActionContext(context)
      const { workspaceId, testId, sharedLinkHash } = payload

      const survey = await api.getSurvey(workspaceId, testId, sharedLinkHash)

      if (!survey) {
        console.error(`No available surveys for test: ${workspaceId}/${testId}`)
        return false
      }

      const { respondents, questions } = survey
      commit.setSurvey(survey)
      // TODO:COLLIN eventually this mapping could or should live on the depths
      const fullRespondents = (respondents || []).map(respondent => {
        const responsesWithAnswers = respondent.responses?.map(response => {
          const question = questions!.find(question => question.id === response.questionId)
          return addAnswerToResponse(response, question!.type as QuestionType)
        })
        return {
          ...respondent,
          responses: responsesWithAnswers
        }
      })
      commit.setRespondents(fullRespondents)
      await dispatch.processQuestions(questions || [])
      commit.setRespondentRemovalMap(generateRespondentToggleMap(respondents!))
      commit.setResponseRemovalMap(generateResponseToggleMap(getters.unfilteredResponses))
      rootDispatch.surveyConfiguration.addDefaultsForUnconfiguredQuestions(questions || [])
      rootCommit.livechartRefs.resetRefMap()
      return true
    },
    async processQuestions (context, questions: Question[]) {
      const { commit } = surveyActionContext(context)
      const sortedQuestions = questions.slice().sort((a, b) => (a.order > b.order) ? 1 : -1) || []

      // Add a second display order for "real" questions
      const orderableQuestions = sortedQuestions.filter(question => {
        const hasChart = (
          question.type !== QuestionType.Variant &&
          question.type !== QuestionType.Description
        )
        const isSharedView = router.currentRoute.name === sharedViewRouteName
        const isHidden = question.configuration && !question.configuration.visible
        if (isHidden && isSharedView) { // Skip hidden questions for shared view ordering
          return false
        } else {
          return hasChart
        }
      })

      orderableQuestions.forEach((question, idx) => {
        question.displayOrder = idx + 1
      })

      // Used to pass on proper respondent groups
      let variantQuestionId = ''
      for (const question of sortedQuestions) {
        if (question.type === QuestionType.Variant) {
          variantQuestionId = question.id
          continue
        }
        question.variantRelationId = variantQuestionId
      }
      commit.setQuestions(sortedQuestions)
    },
    async updateRespondentList (context, payload: { responseRemovalMap: SurveyState['responseRemovalMap'], respondentRemovalMap: SurveyState['respondentRemovalMap'] }) {
      const { state, rootState, commit } = surveyActionContext(context)
      const { responseRemovalMap, respondentRemovalMap } = payload
      const responseChanges = updatedDiff(state.responseRemovalMap, responseRemovalMap) as Record<string, boolean>
      const respondentChanges = updatedDiff(state.respondentRemovalMap, respondentRemovalMap) as Record<string, boolean>
      const mapEvents = (events: Record<string, boolean>) => {
        return Object.entries(events).map(([id, added]) => {
          return {
            id,
            status: added ? graph.RemovalStatus.Added : graph.RemovalStatus.Removed
          }
        })
      }
      try {
        const response = await api.manageResponses({
          workspaceId: rootState.workspace.workspace.id,
          surveyId: state.survey!.id,
          payload: {
            respondents: mapEvents(respondentChanges),
            responses: mapEvents(responseChanges)
          }
        })
        if (response.success) {
          commit.setRespondentRemovalMap(respondentRemovalMap)
          commit.setResponseRemovalMap(responseRemovalMap)
        }
        return true
      } catch (error) {
        console.error(error)
        return false
      }
    }
  }
})

export default survey
export const surveyGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, survey)
export const surveyActionContext = (context:any) => moduleActionContext(context, survey)
